import React, {
  ChangeEvent,
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import THEME from 'theme';
import { Heading, Message, Banner } from 'components/chroma';
import { SubmitButton } from 'components';
import ICONS from 'components/Icons';

import {
  DateOfBirthQuestion,
  InputFormatQuestion,
} from 'scenes/common/components/Questions';

import { SCREEN } from 'scenes/common/types/screen';

import RetrieveQuoteQuestion from 'scenes/common/components/Questions/RetrieveQuoteQuestion';
import { QuoteNumberPrefixes } from 'scenes/retrieve/types';
import DOBErrorMessage from 'scenes/common/components/Questions/DateOfBirthQuestion/DOBErrorMessage';
import { ErrorMessage } from 'scenes/payments/components/Errors';
import { GlobalErrors } from 'scenes/payments/components/Errors/types';
import config from 'shared/config';
import Image from './Image';

const QUOTE_NUMBER = 'quoteNumber';
const POSTCODE = 'postcode';
const DOB = 'dob';

const API_ERROR = 'api-error';

export const POSTCODE_LENGTH = 4;
const MIN_AGE = 16;
const MAX_AGE = 100;

const Wrapper = styled.div`
  margin-top: 40px;

  @media (min-width: ${SCREEN.SMALL}px) {
    margin-top: 70px;
    text-align: left;
  }
`;

type ErrorWrapperProps = {
  isBottom?: boolean;
};

const ErrorWrapper = styled.div`
  margin-bottom: ${(props: ErrorWrapperProps) =>
        props.isBottom ? `2.5rem;` : `1rem;`}
      .c-text,
    .c-heading {
    color: ${THEME.TEXT.ERROR} !important;
  }

  .c-heading {
    margin-bottom: 0.5rem;
    padding-top: 0.05rem;
  }

  .c-icon {
    margin: 0px;
  }
`;

const DobQuestionWrapper = styled.div`
  margin-bottom: 2.5rem;
  .p-form-group {
    margin-bottom: 0;
  }
`;

const getIcon = (quoteNumber: string | null): string => {
  if (!quoteNumber || quoteNumber.length < 4) {
    return ICONS.DEFAULT.UNIFIED;
  }

  switch (quoteNumber.slice(0, 4)) {
    case QuoteNumberPrefixes.COMO:
      return ICONS.DEFAULT.MOTOR;
    case QuoteNumberPrefixes.COHO:
      return ICONS.DEFAULT.HOME;
    case QuoteNumberPrefixes.COLA:
      return ICONS.DEFAULT.LANDLORD;
    default:
      return ICONS.DEFAULT.UNIFIED;
  }
};

export enum FormFlows {
  RETRIEVE = 'RETRIEVE',
  PAYMENT = 'PAYMENT',
}

const getEntityType = (quoteNumber: string | null): string => {
  if (quoteNumber == null) return 'policy holder';
  switch (quoteNumber.slice(0, 4)) {
    case QuoteNumberPrefixes.COMO:
      return 'main driver';
    case QuoteNumberPrefixes.COHO:
    case QuoteNumberPrefixes.COLA:
    default:
      return 'policy holder';
  }
};

type RetrieveQuoteProps = {
  errorMessage?: string;
  errorMessageElement?: JSX.Element | null;
  defaultQuoteNumber: string | null;
  displayDOBQuestion?: boolean;
  onSubmitCallback?: Function;
  handleValidationCallback?: Function;
  buttonText?: string;
  questionHeader?: string;
  header?: string;
  formFlowType?: FormFlows.RETRIEVE | FormFlows.PAYMENT;
};
const RetrieveForm: FunctionComponent<RetrieveQuoteProps> = ({
  errorMessage,
  errorMessageElement,
  defaultQuoteNumber,
  displayDOBQuestion = true,
  onSubmitCallback,
  buttonText = 'Retrieve quote',
  questionHeader = 'Quote number',
  header = 'Retrieve a saved quote',
  formFlowType = FormFlows.RETRIEVE,
  handleValidationCallback,
}) => {
  const {
    formState: hooksForm,
    handleSubmit,
    errors,
    control,
    getValues,
    setError,
    clearErrors,
  } = useForm({
    mode: 'onChange',
  });

  const [icon, setIcon] = useState<string>(getIcon(defaultQuoteNumber));
  const [dobValue, setDob] = useState<string>('');
  const [dobError, setDobError] = useState<boolean>(false);
  const [entityType, setEntityType] = useState<string>(
    getEntityType(defaultQuoteNumber)
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, onChange: Function) => {
      clearErrors(API_ERROR);
      onChange(event);
      if (handleValidationCallback)
        handleValidationCallback(hooksForm.isValid, event);
    },
    [clearErrors, handleValidationCallback, hooksForm.isValid]
  );

  const handleDOBChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, onChange: Function) => {
      setDob((event as unknown) as string);

      clearErrors(API_ERROR);
      onChange(event);
    },
    [clearErrors]
  );

  const handleQuoteNumberChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, onChange: Function) => {
      // event is string actually, not ChangeEvent type
      setIcon(getIcon((event as unknown) as string));
      setEntityType(getEntityType((event as unknown) as string));

      clearErrors(API_ERROR);
      onChange(event);
    },
    [clearErrors]
  );

  const onSubmit = useCallback(() => {
    const { quoteNumber, postcode, dob } = getValues();
    if (onSubmitCallback) {
      onSubmitCallback(quoteNumber, postcode, dob);
    }
  }, [getValues, onSubmitCallback]);

  useEffect(() => {
    if (errorMessageElement) {
      setError(API_ERROR, {
        type: 'manual',
        message: errorMessageElement ? 'API error' : undefined,
      });
    }
  }, [errorMessage, errorMessageElement, setError]);

  const displayGenericErrorMessage =
    config.featureToggles.paymentPortalErrorMessage === 'ON';

  const enableSubmitButton = hooksForm.isValid && !dobError;
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Banner width="viewport" padding={0}>
        <Image icon={icon} />
      </Banner>
      <Wrapper>
        <Heading size={1}>{header}</Heading>
        {displayGenericErrorMessage &&
          !errorMessageElement &&
          formFlowType === FormFlows.PAYMENT && (
            <ErrorWrapper isBottom={false}>
              <ErrorMessage type={GlobalErrors.loginMessage} data={undefined} />
            </ErrorWrapper>
          )}
        {errorMessageElement && formFlowType === FormFlows.PAYMENT && (
          <ErrorWrapper isBottom={false}>{errorMessageElement}</ErrorWrapper>
        )}
        <RetrieveQuoteQuestion
          question={questionHeader}
          placeholder="CO__ - XXXX - XXXX"
          stateKey={QUOTE_NUMBER}
          errors={errors}
          control={control}
          defaultValue={defaultQuoteNumber || ''}
          changeHandler={handleQuoteNumberChange}
        />
        <InputFormatQuestion
          question="Postcode"
          size={4}
          isNumericString
          errors={errors}
          ariaRequired
          format="####"
          placeholder="e.g. 3000"
          control={control}
          stateKey={POSTCODE}
          defaultValue=""
          validationRules={{ required: true, minLength: POSTCODE_LENGTH }}
          ariaDescribedBy={
            formFlowType === FormFlows.PAYMENT ? 'postcode-help' : undefined
          }
          nonClickableHelpText={
            formFlowType === FormFlows.PAYMENT
              ? 'This is the postcode of your insured property or car'
              : undefined
          }
          changeHandler={handleChange}
        />

        {displayDOBQuestion && (
          <DobQuestionWrapper>
            <DateOfBirthQuestion
              question="Date of birth"
              minAge={MIN_AGE}
              maxAge={MAX_AGE}
              entityType={entityType}
              defaultValue=""
              stateKey={DOB}
              disableRangeValidator
              errors={errors}
              control={control}
              changeHandler={handleDOBChange}
            />
            <DOBErrorMessage
              dobValue={dobValue}
              entityType={entityType}
              minAge={MIN_AGE}
              maxAge={MAX_AGE}
              setError={setDobError}
            />
          </DobQuestionWrapper>
        )}
      </Wrapper>

      {formFlowType === FormFlows.RETRIEVE &&
        errors[API_ERROR] &&
        errorMessageElement && (
          <ErrorWrapper isBottom>
            <Message error>{errorMessageElement}</Message>
          </ErrorWrapper>
        )}

      <SubmitButton enabled={enableSubmitButton}>{buttonText}</SubmitButton>
    </form>
  );
};

export default RetrieveForm;
